import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import Seo from "@/components/base/Seo";
import { mangaList, type Manga } from "@/mocks/manga";
import { getCategory, sortByPopularity } from "@/utils/manga";
import MangaCard from "@/components/feature/MangaCard";

type ListingType = "new" | "completed" | "free";

const configs: Record<
  ListingType,
  { title: string; eyebrow: string; subtitle: string; desc: string; keywords: string; badge: string; icon: string }
> = {
  new: {
    title: "新作推荐",
    eyebrow: "新鲜出炉",
    subtitle: "每周精选近期上线的高人气漫画新作，第一时间追上最新精彩。",
    desc: "歪歪漫画网新作推荐，恋爱、热血、悬疑、治愈等全题材最新上线漫画一览，评分与人气实时更新，帮你快速挑选值得追的新番。",
    keywords: "歪歪漫画网,新作推荐,最新漫画,漫画新番,新连载",
    badge: "新作",
    icon: "ri-sparkling-2-line",
  },
  completed: {
    title: "完结作品",
    eyebrow: "一次看到底",
    subtitle: "经典高分完结漫画专区，完整剧情一次读完，告别追更焦虑。",
    desc: "歪歪漫画网完结作品专区，覆盖全题材高分完结漫画，无需追更，一次性享受完整剧情与精彩结局。",
    keywords: "歪歪漫画网,完结作品,完结漫画,高分漫画,漫画大全",
    badge: "完结",
    icon: "ri-check-double-line",
  },
  free: {
    title: "免费专区",
    eyebrow: "零门槛畅读",
    subtitle: "精选高人气免费漫画，恋爱、热血、悬疑、治愈全都有，追更轻松无负担。",
    desc: "歪歪漫画网免费专区，海量可免费阅读的高人气漫画，涵盖恋爱、热血、悬疑、治愈等题材，一键直达免费看。",
    keywords: "歪歪漫画网,免费专区,免费漫画,免费看漫画,漫画阅读",
    badge: "免费",
    icon: "ri-vip-crown-2-line",
  },
};

const sortOptions = [
  { key: "popularity", label: "人气最高" },
  { key: "rating", label: "评分最高" },
] as const;
type SortKey = (typeof sortOptions)[number]["key"];

export default function ListingPage({ type }: { type: ListingType }) {
  const cfg = configs[type];
  const [catFilter, setCatFilter] = useState("全部");
  const [sortKey, setSortKey] = useState<SortKey>("popularity");

  const list = useMemo(() => {
    let base = mangaList.filter((m) => {
      if (type === "new") return m.isNew;
      if (type === "completed") return m.status === "已完结";
      return m.isFree;
    });
    if (catFilter !== "全部") base = base.filter((m) => m.category === catFilter);
    if (sortKey === "rating") return [...base].sort((a, b) => b.rating - a.rating);
    return sortByPopularity(base);
  }, [type, catFilter, sortKey]);

  const catNames = useMemo(() => {
    const set = new Set(
      mangaList
        .filter((m) => (type === "new" ? m.isNew : type === "completed" ? m.status === "已完结" : m.isFree))
        .map((m) => m.category)
    );
    return ["全部", ...[...set].map((c) => getCategory(c).name)];
  }, [type]);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `歪歪漫画网 ${cfg.title}`,
      url: `https://panelpal.github.io/${type}`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://panelpal.github.io/" },
        {
          "@type": "ListItem",
          position: 2,
          name: cfg.title,
          item: `https://panelpal.github.io/${type}`,
        },
      ],
    },
  ];

  return (
    <MainLayout>
      <Seo title={`歪歪漫画网${cfg.title} - 热门${cfg.badge}漫画直达`} description={cfg.desc} keywords={cfg.keywords} canonical={`/${type}`} jsonLd={jsonLd} />

      <section className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{cfg.title}</span>
          </nav>
          <div className="mt-4 flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500 text-2xl text-background-50">
              <i className={cfg.icon} />
            </span>
            <div>
              <span className="text-xs font-medium uppercase tracking-wider text-primary-600">{cfg.eyebrow}</span>
              <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">{cfg.title}</h1>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-sm text-foreground-600">{cfg.subtitle}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-1 overflow-x-auto rounded-full border border-background-200/80 bg-background-100 p-1">
            {catNames.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCatFilter(c)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  catFilter === c ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-full border border-background-200/80 bg-background-100 p-1">
            {sortOptions.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSortKey(s.key)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  sortKey === s.key ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-4 text-sm text-foreground-500">共 {list.length} 部{cfg.badge}作品</p>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
          {list.map((m) => (
            <MangaCard key={m.id} manga={m} />
          ))}
        </div>
      </section>
    </MainLayout>
  );
}